import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HiOutlineEnvelope, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeSlash, HiOutlineShieldCheck, HiOutlineDevicePhoneMobile } from "react-icons/hi2";
import { FaArrowRight } from "react-icons/fa6";
import BrandMark, { makeBadgeIcon } from "./BrandMark";
import { useBrand, brandWords } from "./brand";
import CircularBadge from "../components/badge";
import { authAPI, setToken } from "../services/api";
import "./AdminLogin.css";

/* The line under the headline. Forge runs an application pipeline; Makua
   sells retreats and workshops — so the panel shouldn't promise either one
   things it doesn't do. */
const BLURBS = {
  forge: "Manage applications, supporters, and the journey of those who serve.",
  makua: "Manage retreats, bookings, workshops and the people coming to them.",
};

export default function AdminLogin() {
  const brand = useBrand();
  const words = brandWords(brand.name);
  const badgeIcon = useMemo(() => makeBadgeIcon(brand.product, brand.short), [brand.product, brand.short]);
  const blurb = BLURBS[brand.product] || `Manage everything behind ${brand.name}.`;
  /* Just a hint in the empty field — fall back to something neutral if the
     API hasn't answered or the site isn't a real domain yet. */
  const host = String(brand.site || "").replace(/^https?:\/\//, "").split("/")[0];
  const emailHost = host.includes(".") ? host : "example.com";
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Sent here because the account was switched off or signed out everywhere: say why (once).
  const [error, setError] = useState(() => {
    try { const why = sessionStorage.getItem("admin_signout_reason") || ""; sessionStorage.removeItem("admin_signout_reason"); return why; } catch (_) { return ""; }
  });
  // Two-step: after the password, the code from their authenticator app
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const navigate = useNavigate();

  const signedIn = (data, message) => {
    setToken(data.token);
    if (data.recoveryCodesLeft !== undefined) window.alert(`${message} You have ${data.recoveryCodesLeft} recovery codes left; if that's few, turn two-step off and on again in Security for a new set.`);
    navigate(data.mustSetUp2Step ? "/admin/security" : "/admin/dashboard");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await authAPI.login(email, pw);
      if (res.data.mfaRequired) { setMfaToken(res.data.mfaToken); setCode(""); return; }
      signedIn(res.data);
    } catch (err) {
      setError(err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await authAPI.verifyTwoStep(mfaToken, code.trim());
      signedIn(res.data, res.message);
    } catch (err) {
      setError(err.message || "That code isn't right.");
      // The five-minute step ran out: back to the password
      if (/expired|^Sign in again/i.test(err.message || "")) { setMfaToken(null); setPw(""); }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="alog">
      <div className="alog__left">
        <div className="alog__left-bg" aria-hidden="true">
          <img src="/assets/hero.png" alt="" className="alog__bg-img" />
          <div className="alog__bg-overlay" />
        </div>
        <div className="alog__left-content">
          <div className="alog__left-logo">
            <BrandMark product={brand.product} short={brand.short} fill="var(--admin-rust)" width={52} height={52} />
            <div>
              <p className="alog__brand">{words[0]}</p>
              <p className="alog__brand-sub">{words.slice(1).join(" ")}</p>
            </div>
          </div>
          <div className="alog__left-hero">
            <h1 className="alog__headline">
              Admin Portal.
              <br />
              <span className="alog__headline-rust">Mission Control.</span>
            </h1>
            <span className="alog__rule" aria-hidden="true" />
            <p className="alog__hero-sub">{blurb}</p>
          </div>
          <div className="alog__badge">
            <CircularBadge text={`${brand.tagline} ·`} colors="rgba(255,255,255,0.45)" logoSize={28} icon={badgeIcon} />
          </div>
        </div>
      </div>

      <div className="alog__right">
        <motion.div className="alog__card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <div className="alog__card-mtn" aria-hidden="true">
            <img src="/assets/mountainsForst.png" alt="" />
          </div>
          <div className="alog__card-badge">
            <HiOutlineShieldCheck className="alog__shield" />
            <span>Admin Access</span>
          </div>
          <h2 className="alog__card-title">{mfaToken ? "Two-step sign-in" : "Welcome Back"}</h2>
          <p className="alog__card-sub">
            {mfaToken
              ? useRecovery ? "Enter one of the recovery codes you saved when you set up two-step." : "Open your authenticator app and enter the 6-digit code for this account."
              : `Sign in to your admin account to manage ${brand.name}.`}
          </p>

          {error && <p className="alog__error" role="alert">{error}</p>}

          {mfaToken ? (
            <form className="alog__form" onSubmit={handleCode} noValidate>
              <div className="alog__field">
                <label className="alog__label" htmlFor="a-code">{useRecovery ? "Recovery code" : "Code"}</label>
                <div className="alog__input-wrap">
                  <HiOutlineDevicePhoneMobile className="alog__input-icon" />
                  <input id="a-code" className="alog__input alog__input--code" value={code} autoFocus
                    inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" maxLength={useRecovery ? 9 : 6}
                    placeholder={useRecovery ? "xxxx-xxxx" : "123456"}
                    onChange={(e) => { setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, "")); setError(""); }} />
                </div>
              </div>
              <button type="submit" className="alog__submit" disabled={loading || (useRecovery ? code.trim().length < 8 : code.length < 6)}>
                {loading ? "Checking…" : <><span>Verify</span><FaArrowRight /></>}
              </button>
              <div className="alog__alt-row">
                <button type="button" className="alog__link-btn" onClick={() => { setUseRecovery((v) => !v); setCode(""); setError(""); }}>
                  {useRecovery ? "Use the code from my app" : "Lost your phone? Use a recovery code"}
                </button>
                <button type="button" className="alog__link-btn" onClick={() => { setMfaToken(null); setCode(""); setError(""); }}>Start again</button>
              </div>
            </form>
          ) : (
          <form className="alog__form" onSubmit={handleLogin} noValidate>
            <div className="alog__field">
              <label className="alog__label" htmlFor="a-email">
                Email Address
              </label>
              <div className="alog__input-wrap">
                <HiOutlineEnvelope className="alog__input-icon" />
                <input id="a-email" type="email" className="alog__input" placeholder={`admin@${emailHost}`} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
              </div>
            </div>
            <div className="alog__field">
              <div className="alog__label-row">
                <label className="alog__label" htmlFor="a-pw">
                  Password
                </label>
                <Link to="/admin/forgot" className="alog__forgot">
                  Forgot password?
                </Link>
              </div>
              <div className="alog__input-wrap">
                <HiOutlineLockClosed className="alog__input-icon" />
                <input
                  id="a-pw"
                  type={showPw ? "text" : "password"}
                  className="alog__input"
                  placeholder="Enter your password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button type="button" className="alog__pw-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                </button>
              </div>
            </div>
            <button type="submit" className="alog__submit" disabled={loading}>
              {loading ? (
                "Signing in…"
              ) : (
                <>
                  <span>Sign In</span>
                  <FaArrowRight />
                </>
              )}
            </button>
          </form>
          )}
          <p className="alog__back">
            <Link to="/">← Return to main site</Link>
          </p>
        </motion.div>
        <div className="alog__security">
          <HiOutlineShieldCheck />
          <span>Secure admin access — all sessions are encrypted and logged.</span>
        </div>
      </div>
    </div>
  );
}
